import { format } from 'date-fns';
import { getCustomerRenderScope, getStoredCustomerData } from './auth/customerIdentity';

// "Fri May 31 2024 00:00:00 GMT+0530 (India Standard Time)" ===>  "31/05/2024"
export function CovertString_TO_DateFormat(currdate) {
    return format(new Date(currdate), 'dd/MM/yyyy');
}

export function isPastDate(dateString) {
  // Parse the input string into a Date object
  const inputDate = new Date(dateString);
  const today = new Date();

  // Normalize both dates to midnight (ignore time part)
  inputDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  return inputDate <= today; // true if past date
}


export function openInGoogleMaps(address) {
    const encodedAddress = encodeURIComponent(address);
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
    window.open(mapsUrl, '_blank');
};

const hostname = String(window.location.hostname || "").toLowerCase();
export const isLocalhost = Boolean(
  hostname === "localhost" ||
  hostname === "[::1]" ||
  hostname === "127.0.0.1"
);
const isVpnWebHost = hostname === "dev.partywitty.com";
const useVpnNetworkEndpoints = isLocalhost || isVpnWebHost;

const fallbackApiBaseUrl = useVpnNetworkEndpoints ? "https://dashboard.partywitty.com" : "https://admin.partywitty.com";
const fallbackNodeApiBaseUrl = useVpnNetworkEndpoints ? "https://vpnnode.partywitty.com" : "https://node.partywitty.com";

export const API_BASE_URL = String(process.env.REACT_APP_API_BASE_URL || fallbackApiBaseUrl).replace(/\/+$/, "");
const absoluteNodeApiBaseUrl = String(process.env.REACT_APP_NODE_API_BASE_URL || fallbackNodeApiBaseUrl).replace(/\/+$/, "");
const absolutePaymentApiBaseUrl = String(
  process.env.REACT_APP_PAYMENT_API_BASE_URL || API_BASE_URL,
).replace(/\/+$/, "");

// On localhost dev the customer session is a third-party `.partywitty.com` cookie
// that Chrome blocks, so authenticated calls 401 and the app shows "Session
// Expired". Route the Node API and the customer-auth API through the CRA dev
// proxy (see setupProxy.js) which rewrites the cookie to first-party localhost.
// API_BASE_URL stays absolute because it also builds asset/media URLs.
export const NODE_API_BASE_URL = isLocalhost ? "/__pw_node" : absoluteNodeApiBaseUrl;
// The public feed is intentionally available before sign-in. Local development
// normally proxies /__pw_node to a developer's local Node service, which may
// not be running; route anonymous feed reads to the public Node API instead.
export const PUBLIC_NODE_API_BASE_URL = isLocalhost
  ? "/__pw_public_node"
  : absoluteNodeApiBaseUrl;
export const CUSTOMER_API_BASE_URL = isLocalhost ? "/__pw_api" : API_BASE_URL;
// Only payment initiation uses this base. Local development routes it through
// the dedicated Node gateway; production must point it at the deployed gateway
// front door (for example api.partywitty.com), not directly at PHP.
export const PAYMENT_API_BASE_URL = isLocalhost
  ? "/__pw_gateway"
  : absolutePaymentApiBaseUrl;
export const FRONTEND_BASE_URL = "https://partywitty.com";
const rawMediaBase = String(process.env.REACT_APP_MEDIA_BASE_URL || `${API_BASE_URL}/assets/uploads`).trim();
export const MEDIA_BASE_URL = rawMediaBase.replace(/\/+$/, "");
export const MEDIA_BASE_URL_WITH_SLASH = `${MEDIA_BASE_URL}/`;

function normalizeAbsoluteMediaUrl(rawValue) {
  let value = String(rawValue || "").trim();
  if (!/^https?:\/\//i.test(value)) {
    return value;
  }

  const baseValue = String(MEDIA_BASE_URL || "").toLowerCase();
  const baseHasAssetsUploads = baseValue.includes("/assets/uploads");
  if (!baseHasAssetsUploads) {
    try {
      const parsed = new URL(value);
      if (/^\/assets\/uploads\//i.test(parsed.pathname)) {
        parsed.pathname = parsed.pathname.replace(/^\/assets\/uploads\//i, "/");
        value = parsed.toString();
      } else if (/^\/uploads\//i.test(parsed.pathname)) {
        parsed.pathname = parsed.pathname.replace(/^\/uploads\//i, "/");
        value = parsed.toString();
      }
    } catch {
    }
  }

  if (MEDIA_BASE_URL) {
    const base = MEDIA_BASE_URL;
    const lowerValue = value.toLowerCase();
    const lowerBase = base.toLowerCase();
    if (lowerValue.startsWith(lowerBase)) {
      const suffix = value.slice(base.length);
      if (suffix && !suffix.startsWith("/")) {
        value = `${base}/${suffix.replace(/^\/+/, "")}`;
      }
    }
  }

  return value.replace(/(\.[a-z0-9]{2,8})\/+$/i, "$1");
}

// Returns a safe @handle string. Returns '' if the value looks like a JWT or internal PWU code.
export function safeUsername(val) {
  if (!val) return '';
  const str = String(val).trim().replace(/^@+/, '');
  if (!str) return '';
  if (str.startsWith('eyJ') || /^PWU[0-9a-zA-Z_]{4,}/.test(str) || /^user_\d+$/.test(str)) return '';
  return `@${str}`;
}

// Capitalises each word and collapses extra whitespace for display purposes only.
export function sanitizeName(val) {
  if (!val) return '';
  return String(val)
    .trim()
    .replace(/\s+/g, ' ')
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

export function getMediaUrl(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  // Admin server → R2 redirect
if (raw.includes('admin.partywitty.com/assets/uploads/')) {
  return raw.replace(
    'https://admin.partywitty.com/assets/uploads/',
    'https://media.partywitty.com/'
  );
}
  if (/^https?:\/\//i.test(raw)) return normalizeAbsoluteMediaUrl(raw);

  let normalized = raw.replace(/\\/g, "/").replace(/[?#].*$/, "").replace(/^\/+/, "");
  const baseValue = String(MEDIA_BASE_URL || "").toLowerCase();
  const baseHasAssetsUploads = baseValue.includes("/assets/uploads");
  const baseHasUploads = /\/uploads(\/|$)/i.test(baseValue);
  const baseHasPublicQr = baseValue.includes("/public/qr_codes");
  if (normalized.startsWith("master/")) {
    normalized = normalized.slice("master/".length);
  }

  const conditionalPrefixes = [];
  if (baseHasAssetsUploads) conditionalPrefixes.push("assets/uploads/");
  if (baseHasUploads) conditionalPrefixes.push("uploads/");
  if (baseHasPublicQr) conditionalPrefixes.push("public/qr_codes/");

  for (const prefix of conditionalPrefixes) {
    if (normalized.startsWith(prefix)) {
      normalized = normalized.slice(prefix.length);
      break;
    }
  }

  if (!baseHasAssetsUploads && normalized.startsWith("assets/uploads/")) {
    normalized = normalized.slice("assets/uploads/".length);
  }
  if (!baseHasUploads && normalized.startsWith("uploads/")) {
    normalized = normalized.slice("uploads/".length);
  }

  return `${MEDIA_BASE_URL}/${normalized.replace(/^\/+/, "")}`;
}

// getMediaUrl() rewrites admin.partywitty.com/assets/uploads/<file> to the R2 CDN
// domain (media.partywitty.com/<file>). The R2 migration is still in progress, so
// the CDN doesn't have every file yet and a rewritten URL can 404 while the
// original admin.partywitty.com URL still serves it fine. This reverses that
// rewrite so <img>/<video> onError handlers can retry the original URL before
// giving up.
export function getMediaRewriteFallback(resolvedUrl) {
  const match = /^https:\/\/media\.partywitty\.com\/(.+)$/i.exec(
    String(resolvedUrl || "").trim(),
  );
  if (!match) return "";
  return `https://admin.partywitty.com/assets/uploads/${match[1]}`;
}

// Generic onError handler for <img>/<video> rendered via getMediaUrl(): retries
// the pre-rewrite admin.partywitty.com URL once, then hands off to onExhausted().
export function handleMediaLoadError(event, onExhausted) {
  const el = event.target;
  const fallback = getMediaRewriteFallback(el.src);
  if (fallback && el.src !== fallback && !el.dataset.mediaFallbackApplied) {
    el.dataset.mediaFallbackApplied = "1";
    el.src = fallback;
    return;
  }
  if (onExhausted) onExhausted(event);
}

export function toPackageSlug(value) {
  const text = String(value || "").trim().toLowerCase();
  if (!text) return "";
  return text
    .replace(/\+/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}


export function formatDuration(text) {
  if (!text) return "";

  return text
    .replace(/Hours?/i, "hr")      // Replace "Hour" or "Hours" → "hr"
    .replace(/Minutes?/i, "m")   // Replace "Minute" or "Minutes" → "min"
    .replace(/\s+/g, " ")          // Normalize spaces
    .trim();                       // Remove trailing space
}

// ─── Club-favourite localStorage helpers ────────────────────────────────────
const CLUB_FAV_KEY = "partywitty_club_favorites_v2";
const CLUB_FAV_CACHE_TTL = 30 * 60 * 1000;

function readClubFavouriteCache() {
  const owner = getCustomerRenderScope(getStoredCustomerData());
  if (!owner) return [];
  try {
    const cache = JSON.parse(localStorage.getItem(CLUB_FAV_KEY) || "null");
    return cache?.owner === owner && Date.now() - cache.updatedAt < CLUB_FAV_CACHE_TTL && Array.isArray(cache.ids) ? cache.ids : [];
  } catch {
    return [];
  }
}

export function isClubFavourited(clubId, serverValue) {
  if (!clubId) return false;
  if (serverValue === true || serverValue === 1 || serverValue === "1") return true;
  if (serverValue === false || serverValue === 0 || serverValue === "0") return false;
  try {
    const ids = readClubFavouriteCache();
    return ids.includes(String(clubId));
  } catch {
    return false;
  }
}

export function setClubFavourited(clubId, fav, expectedOwner = getCustomerRenderScope(getStoredCustomerData())) {
  if (!clubId) return;
  try {
    const owner = getCustomerRenderScope(getStoredCustomerData());
    if (!owner || owner !== expectedOwner) return;
    let ids = readClubFavouriteCache();
    const id = String(clubId);
    ids = fav ? (ids.includes(id) ? ids : [id, ...ids]) : ids.filter((x) => x !== id);
    localStorage.setItem(CLUB_FAV_KEY, JSON.stringify({ owner, ids: ids.slice(0, 200), updatedAt: Date.now() }));
    window.dispatchEvent(new CustomEvent("partywitty:club-favorites-updated"));
  } catch {}
}
